import { getLocalTimeZone, today } from '@internationalized/date'
import { useState } from 'react'
import {
  CalendarGrid,
  CalendarGridBody,
  type DateValue,
  RangeCalendar as AriaRangeCalendar,
  RangeCalendarContext,
  type RangeCalendarProps as AriaRangeCalendarProps,
  useSlottedContext,
} from 'react-aria-components'
import { useIntl } from 'react-intl'

import { Stack, Typography } from '../../../structure'
import { CalendarCell } from './CalendarCell'
import { CalendarGridHeader } from './CalendarGridHeader'
import { CalendarHeader } from './CalendarHeader'
import { clampCalendarDate } from './utils'

type Props = Pick<
  AriaRangeCalendarProps<DateValue>,
  'value' | 'onChange' | 'defaultFocusedValue' | 'onFocusChange'
>

export const RangeCalendar = ({
  defaultFocusedValue,
  onFocusChange,
  ...calendarProps
}: Props) => {
  const { formatDate } = useIntl()
  const context = useSlottedContext(RangeCalendarContext)
  const minValue = context?.minValue
  const maxValue = context?.maxValue
  const isDateUnavailable = context?.isDateUnavailable
  // React Aria only bounds keyboard focus via minValue/maxValue, which we strip to keep the viewport stable.
  const [focusedValue, setFocusedValue] = useState(() =>
    clampCalendarDate({
      date:
        defaultFocusedValue ??
        calendarProps.value?.start ??
        context?.value?.start ??
        today(getLocalTimeZone()),
      min: minValue,
      max: maxValue,
    }),
  )

  return (
    <RangeCalendarContext.Provider
      value={{ ...context, minValue: undefined, maxValue: undefined }}
    >
      <AriaRangeCalendar
        {...calendarProps}
        focusedValue={focusedValue}
        onFocusChange={(date) => {
          const clampedDate = clampCalendarDate({
            date,
            min: minValue,
            max: maxValue,
          })
          setFocusedValue(clampedDate)
          onFocusChange?.(clampedDate)
        }}
        selectionAlignment="start"
        isDateUnavailable={(date, range) =>
          Boolean(
            (minValue && date.compare(minValue) < 0) ||
            (maxValue && date.compare(maxValue) > 0) ||
            isDateUnavailable?.(date, range),
          )
        }
        visibleDuration={{ months: 3 }}
        pageBehavior="single"
        weeksInMonth={6}
      >
        {({ state: { visibleRange } }) => (
          <>
            <CalendarHeader
              minValue={minValue}
              maxValue={maxValue}
              visibleRange={visibleRange}
            />
            <Stack gap="spacious">
              {Array.from({ length: 3 }, (_, monthOffset) => {
                const monthLabel = formatDate(
                  visibleRange.start
                    .add({ months: monthOffset })
                    .toDate(getLocalTimeZone()),
                  { month: 'long' },
                )

                return (
                  <Stack className="flex-col" key={monthOffset}>
                    <Typography
                      className="text-center"
                      as="h2"
                      weight="semibold"
                      intensity="primary"
                    >
                      {monthLabel}
                    </Typography>
                    <CalendarGrid offset={{ months: monthOffset }}>
                      <CalendarGridHeader />
                      <CalendarGridBody className="[&_tr]:h-9">
                        {(date) => <CalendarCell isRange date={date} />}
                      </CalendarGridBody>
                    </CalendarGrid>
                  </Stack>
                )
              })}
            </Stack>
          </>
        )}
      </AriaRangeCalendar>
    </RangeCalendarContext.Provider>
  )
}
